import React, { useEffect, useState, useMemo } from "react"
import PropTypes from "prop-types"
import {
    Button,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    Spinner,
    Badge
} from "reactstrap"
import { useFormik } from "formik"
import * as yup from "yup"
import { useDispatch, useSelector } from "react-redux"
import {
    massMessageTempDelete,
    massMessageTempDeleteReset,
    massMessageTempLoad,
    massMessageTempSave,
    massMessageTempSaveReset
} from "../../redux/actions/BrandActions"
import {
    successAlert,
    errorAlert,
    confirmAlert
} from './CustomAlerts'
import {
    Bookmark,
    FileText,
    Check,
    Trash2,
    X
} from "react-feather"

const MsgTemplates = ({
    showSaveModal,
    template,
    closeModal,
    templateType,
    showTemplatesModal,
    applyTemplate
}) => {
    const dispatch = useDispatch()

    const [openSaveModal, setOpenSaveModal] = useState(false)
    const [openTemplatesModal, setOpenTemplatesModal] = useState(false)

    const {
        massMsgTempSaveLoading,
        massMsgTempSaveData,
        massMsgTempSaveError,

        massMsgTempListLoading,
        massMsgTempListData,

        massMsgTempDeleteLoading,
        massMsgTempDeleteData
    } = useSelector(state => state.brandReducer)

    const saveForm = useFormik({
        initialValues: {
            title: "",
            message: ""
        },
        validationSchema: yup.object().shape({
            title: yup.string().trim().required("This field is required"),
            message: yup.string().trim().required("This field is required")
        }),
        onSubmit: (values) => {
            const obj = {
                title: values.title,
                template: values.message
            }
            if (templateType === 'mass') {
                obj.type = 'mass'
            }
            dispatch(massMessageTempSave(obj))
        }
    })

    const {
        handleSubmit,
        values,
        handleBlur,
        handleChange,
        touched,
        errors,
        resetForm,
        setFieldValue
    } = saveForm

    useEffect(() => {
        dispatch(massMessageTempLoad(templateType === 'mass' ? "?type=mass" : ""))
    }, [dispatch, templateType])

    useEffect(() => {
        setOpenSaveModal(showSaveModal)
        setFieldValue('message', template || '')
    }, [showSaveModal, template, setFieldValue])

    useEffect(() => {
        setOpenTemplatesModal(showTemplatesModal)
        if (showTemplatesModal) {
            dispatch(massMessageTempLoad(templateType === 'mass' ? "?type=mass" : ""))
        }
    }, [showTemplatesModal, templateType, dispatch])

    useEffect(() => {
        if (massMsgTempSaveData?.success) {
            successAlert(massMsgTempSaveData?.msg)
            resetForm()
            closeModal()
            dispatch(massMessageTempLoad(templateType === 'mass' ? "?type=mass" : ""))
            dispatch(massMessageTempSaveReset())
        }

        if (massMsgTempSaveError?.response?.status) {
            const errorHTML = ['<ul class="list-group">']
            if (massMsgTempSaveError?.response?.data?.errors?.title) {
                errorHTML.push(`<li class="list-group-item font-small-3 text-danger">${massMsgTempSaveError?.response?.data?.errors?.title[0]}</li>`)
            }
            errorHTML.push('</ul>')
            errorAlert(errorHTML.join(''))
            dispatch(massMessageTempSaveReset())
        }

        if (massMsgTempDeleteData?.success) {
            successAlert(massMsgTempDeleteData?.msg)
            dispatch(massMessageTempLoad(templateType === 'mass' ? "?type=mass" : ""))
            dispatch(massMessageTempDeleteReset())
        }
    }, [
        massMsgTempSaveData,
        massMsgTempSaveError,
        massMsgTempDeleteData,
        templateType,
        dispatch,
        closeModal,
        resetForm
    ])

    const deleteMassMsgTemp = async (data) => {
        const result = await confirmAlert('Confirm!', 'Do you want to delete this template?')
        if (result) {
            dispatch(massMessageTempDelete({
                id: data.id
            }))
        }
    }

    const templatesList = useMemo(() => {
        const list = massMsgTempListData?.data || []
        if (!templateType) {
            return list
        }
        return list.filter(item => item?.type === templateType)
    }, [massMsgTempListData?.data, templateType])

    return (
        <>
            {/* Save Template Modal */}
            <Modal
                isOpen={openSaveModal}
                toggle={() => {
                    closeModal()
                    resetForm()
                }}
                className="modal-lg modal-dialog-centered msg-template-modal"
                backdrop="static"
            >
                <div className="msg-template-header d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div className="msg-template-icon-badge mr-1 flex-shrink-0">
                            <Bookmark size={20} />
                        </div>
                        <div>
                            <h5 className="mb-0 font-weight-bold" style={{ color: '#1e293b', fontSize: '16px' }}>
                                Save as Message Template
                            </h5>
                            <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                                Save this message draft to reuse it across future broadcasts and direct chats.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="msg-template-close-btn"
                        onClick={() => {
                            closeModal()
                            resetForm()
                        }}
                        disabled={massMsgTempSaveLoading}
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <Form onSubmit={handleSubmit} autoComplete="off">
                    <ModalBody className="p-2" style={{ background: '#f8fafc' }}>
                        <FormGroup className="mb-1.5">
                            <Label htmlFor="massMsgTempTitle" className="font-weight-bold text-dark" style={{ fontSize: '13px' }}>
                                Template Title <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="text"
                                placeholder="e.g. Q4 Brand Partnership Outreach"
                                id="massMsgTempTitle"
                                name="title"
                                value={values.title}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                disabled={massMsgTempSaveLoading}
                                style={{
                                    height: '38px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    borderColor: touched.title && errors.title ? '#ef4444' : '#cbd5e1'
                                }}
                            />
                            {touched.title && errors.title && (
                                <small className="text-danger mt-25 d-block font-weight-bold">
                                    {errors.title}
                                </small>
                            )}
                        </FormGroup>

                        <FormGroup className="mb-0">
                            <Label className="font-weight-bold text-dark" style={{ fontSize: '13px' }}>
                                Message Content Preview
                            </Label>
                            <div
                                className="p-1 border rounded"
                                style={{
                                    background: '#ffffff',
                                    borderColor: '#e2e8f0',
                                    maxHeight: '180px',
                                    overflowY: 'auto',
                                    fontSize: '13px',
                                    lineHeight: '1.5',
                                    color: '#334155'
                                }}
                                dangerouslySetInnerHTML={{ __html: values.message || '<span class="text-muted">No content</span>' }}
                            />
                            {touched.message && errors.message && (
                                <small className="text-danger mt-25 d-block font-weight-bold">
                                    {errors.message}
                                </small>
                            )}
                        </FormGroup>
                    </ModalBody>

                    {/* Better Styled Footer */}
                    <div className="msg-template-footer">
                        <Button
                            type="button"
                            color="secondary"
                            outline
                            size="sm"
                            className="msg-template-action-btn"
                            onClick={() => {
                                closeModal()
                                resetForm()
                            }}
                            disabled={massMsgTempSaveLoading}
                        >
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            type="submit"
                            size="sm"
                            className="msg-template-apply-btn d-inline-flex align-items-center"
                            disabled={massMsgTempSaveLoading}
                        >
                            {massMsgTempSaveLoading ? (
                                <>
                                    <Spinner size="sm" className="mr-50" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Bookmark size={14} className="mr-50" />
                                    Save Template
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* Browse Templates Modal */}
            <Modal
                isOpen={openTemplatesModal}
                toggle={closeModal}
                className="modal-lg modal-dialog-centered msg-template-modal"
                backdrop="static"
            >
                <div className="msg-template-header d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div className="msg-template-icon-badge mr-1 flex-shrink-0">
                            <Bookmark size={20} />
                        </div>
                        <div>
                            <h5 className="mb-0 font-weight-bold" style={{ color: '#1e293b', fontSize: '16px' }}>
                                Browse Message Templates
                            </h5>
                            <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                                Select a pre-saved message template to instantly populate your composer.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="msg-template-close-btn"
                        onClick={closeModal}
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <ModalBody className="p-2" style={{ background: '#f8fafc' }}>
                    {/* Templates List */}
                    <div style={{ maxHeight: '440px', overflowY: 'auto', paddingRight: '2px' }}>
                        {massMsgTempListLoading ? (
                            <div className="text-center py-4">
                                <Spinner color="primary" size="sm" className="mr-50" />
                                <span className="text-muted small">Loading message templates...</span>
                            </div>
                        ) : templatesList.length > 0 ? (
                            templatesList.map((item, index) => {
                                const plainSnippet = (item?.template || '')
                                    .replace(/<[^>]*>?/gm, ' ')
                                    .replace(/\s+/g, ' ')
                                    .trim()

                                return (
                                    <div key={item.id || index} className="msg-template-card">
                                        <div className="d-flex align-items-center justify-content-between flex-wrap mb-50" style={{ gap: '8px' }}>
                                            <div className="d-flex align-items-center flex-grow-1 mr-1" style={{ minWidth: 0 }}>
                                                <FileText size={16} className="text-primary mr-50 flex-shrink-0" />
                                                <h6
                                                    className="mb-0 font-weight-bold text-dark text-truncate"
                                                    style={{ fontSize: '13.5px' }}
                                                    title={item.title}
                                                >
                                                    {item.title}
                                                </h6>
                                                {item?.type === 'mass' ? (
                                                    <Badge color="light-info" pill className="ml-50 flex-shrink-0" style={{ fontSize: '10.5px' }}>
                                                        Broadcast
                                                    </Badge>
                                                ) : (
                                                    <Badge color="light-primary" pill className="ml-50 flex-shrink-0" style={{ fontSize: '10.5px' }}>
                                                        Direct
                                                    </Badge>
                                                )}
                                            </div>

                                            <div className="d-flex align-items-center flex-shrink-0" style={{ gap: '8px' }}>
                                                <button
                                                    type="button"
                                                    className="btn msg-template-apply-btn"
                                                    onClick={() => {
                                                        applyTemplate(item)
                                                        closeModal()
                                                    }}
                                                    disabled={massMsgTempDeleteLoading}
                                                >
                                                    <Check size={14} className="mr-50" />
                                                    Apply
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn msg-template-delete-btn"
                                                    onClick={() => deleteMassMsgTemp(item)}
                                                    disabled={massMsgTempDeleteLoading}
                                                    title="Delete template"
                                                >
                                                    <Trash2 size={14} className="mr-50" />
                                                    Delete
                                                </button>
                                            </div>
                                        </div>

                                        {plainSnippet && (
                                            <p
                                                className="mb-0 text-muted"
                                                style={{
                                                    fontSize: '12px',
                                                    lineHeight: '1.45',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {plainSnippet}
                                            </p>
                                        )}
                                    </div>
                                )
                            })
                        ) : (
                            <div className="text-center py-4">
                                <FileText size={36} className="text-muted mb-50" />
                                <h6 className="font-weight-bold text-dark mb-25" style={{ fontSize: '14px' }}>
                                    No message templates saved yet
                                </h6>
                                <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                                    Save composed messages as templates to reuse them anytime.
                                </p>
                            </div>
                        )}
                    </div>
                </ModalBody>

                {/* Better Styled Footer */}
                <div className="msg-template-footer">
                    <div className="d-flex align-items-center">
                        <span className="text-muted" style={{ fontSize: '12.5px' }}>
                            Available Templates:
                        </span>
                        <Badge color="light-primary" pill className="ml-50" style={{ fontSize: '12px', padding: '4px 10px' }}>
                            {templatesList.length}
                        </Badge>
                    </div>

                    <Button
                        type="button"
                        color="secondary"
                        outline
                        size="sm"
                        className="msg-template-action-btn"
                        onClick={closeModal}
                    >
                        Close
                    </Button>
                </div>
            </Modal>
        </>
    )
}

MsgTemplates.propTypes = {
    showSaveModal: PropTypes.bool,
    template: PropTypes.string,
    closeModal: PropTypes.func.isRequired,
    templateType: PropTypes.string,
    showTemplatesModal: PropTypes.bool,
    applyTemplate: PropTypes.func.isRequired
}

export default MsgTemplates